/* SpawnAnything — rules of the daily battle's leaderboard (pure, no deps besides the blocklist): shared by the browser
   (src/content/daily.js, src/features/campaign.js), the Cloudflare Worker (worker/api.js, KV) and the Node dev server
   (server/index.js, in memory). One entry per player (`id` = hash of IP + day, never the IP itself) and day; a better
   result replaces the old one. The score is how long the army held out (s, seconds, at most DAILY_SECONDS), then the
   surviving units (a) when it held out to the end. */
import { blockedTerm } from './blocklist.js';

export const DAILY_SECONDS = 480;
export const BOARD_MAX = 50;
const NAME_RE = /^[\p{L}\p{N} ._-]{1,16}$/u;

/** UTC day key 'YYYY-MM-DD'. */
export const dayKey = (d = Date.now()) => new Date(d).toISOString().slice(0, 10);
export const isDayKey = s => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s + 'T00:00:00Z')) && dayKey(Date.parse(s + 'T00:00:00Z')) === s;
/** Score of a finished battle: seconds held (capped, held out = DAILY_SECONDS) and survivors when held out. */
export function dailyScore(t, won, alive) {
  return { s: won ? DAILY_SECONDS : Math.max(0, Math.min(DAILY_SECONDS - 1, Math.floor(+t || 0))), a: won ? Math.max(0, alive | 0) : 0 };
}
/** Board order: longer first, then more survivors, then the earlier entry. */
export const dailyCmp = (x, y) => y.s - x.s || y.a - x.a || (x.at || 0) - (y.at || 0);

/** Player name for the board: trimmed, 1-16 letters/digits/space/._- and not on the blocklist; else null. */
export function cleanName(n) {
  if (typeof n !== 'string') return null;
  const v = n.replace(/\s+/g, ' ').trim();
  return NAME_RE.test(v) && !blockedTerm(v) ? v : null;
}

/** Check a submission { day, name, s, a } at time `now` (ms): today's or yesterday's board only (a battle may run over
    midnight). Returns { ok: true, entry: { day, n, s, a } } or { ok: false, error }. */
export function checkEntry(data, now = Date.now()) {
  if (!data || typeof data !== 'object') return { ok: false, error: 'invalid_body' };
  const today = dayKey(now), yesterday = dayKey(now - 864e5);
  if (!isDayKey(data.day) || (data.day !== today && data.day !== yesterday)) return { ok: false, error: 'invalid_day' };
  const n = cleanName(data.name); if (!n) return { ok: false, error: 'invalid_name' };
  const s = data.s, a = data.a ?? 0;
  if (!Number.isInteger(s) || s < 0 || s > DAILY_SECONDS) return { ok: false, error: 'invalid_score' };
  if (!Number.isInteger(a) || a < 0 || a > 3000 || (a > 0 && s < DAILY_SECONDS)) return { ok: false, error: 'invalid_score' };
  return { ok: true, entry: { day: data.day, n, s, a } };
}

/** Put entry (with id, at) into the sorted board; a player's better result replaces the old one, a worse one is
    ignored (also the new name). Returns { list, rank } (rank 1-based, 0 = not on the board). */
export function mergeBoard(list, entry) {
  const L = Array.isArray(list) ? list.filter(e => e && typeof e === 'object') : [];
  const k = L.findIndex(e => e.id === entry.id);
  if (k >= 0) {
    if (dailyCmp(entry, L[k]) >= 0) { const sorted = L.sort(dailyCmp); return { list: sorted, rank: sorted.indexOf(L[k]) + 1, kept: true }; }
    L.splice(k, 1);
  }
  L.push(entry); L.sort(dailyCmp);
  const out = L.slice(0, BOARD_MAX);
  return { list: out, rank: out.indexOf(entry) + 1 };
}
/** What the board shows publicly (no ids). */
export const publicBoard = (list, max = 20) => (Array.isArray(list) ? list : []).slice(0, max).map(e => ({ n: e.n, s: e.s, a: e.a }));
